import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, RefreshCw, CheckSquare } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function AICopyDialog({ property, onApply }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [keywords, setKeywords] = useState("");
  const [tone, setTone] = useState("warm");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const gen = async () => {
    setBusy(true); setResult(null);
    try {
      const kws = keywords.split(",").map(s => s.trim()).filter(Boolean);
      const r = await api.post("/ai/vetrina-copy", { name: property.name, city: property.city, property_type: property.property_type || "bnb", keywords: kws, tone });
      setResult(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || t("dash.toast_error")); }
    finally { setBusy(false); }
  };
  const apply = () => {
    onApply({ description_it: result.description_it, description_en: result.description_en, amenities: result.amenities });
    setOpen(false); setResult(null); setKeywords("");
    toast.success(t("dash.saved"));
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="lift" data-testid="ai-copy-btn"><Sparkles className="w-4 h-4 mr-1"/>{t("dash.ai_copy")}</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{t("dash.ai_copy_title")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">{t("dash.ai_copy_hint")}</p>
          <div><Label>{t("dash.ai_keywords")}</Label><Input value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="vista mare, tramonto, limoncello" data-testid="ai-keywords"/></div>
          <div><Label>{t("dash.ai_tone")}</Label>
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger data-testid="ai-tone"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="warm">Warm</SelectItem>
                <SelectItem value="luxury">Luxury</SelectItem>
                <SelectItem value="rustic">Rustic</SelectItem>
                <SelectItem value="modern">Modern</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={gen} disabled={busy} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid="ai-generate-btn">
            {busy ? <RefreshCw className="w-4 h-4 animate-spin mr-1"/> : <Sparkles className="w-4 h-4 mr-1"/>}
            {t("dash.ai_generate")}
          </Button>
          {result && (
            <div className="border border-border rounded-md p-3 bg-sand space-y-3 max-h-64 overflow-y-auto" data-testid="ai-result">
              <div><div className="text-[10px] uppercase tracking-[0.2em] text-sage mb-1">IT</div><p className="text-sm">{result.description_it}</p></div>
              <div><div className="text-[10px] uppercase tracking-[0.2em] text-sage mb-1">EN</div><p className="text-sm">{result.description_en}</p></div>
              <div className="flex flex-wrap gap-1.5">{result.amenities?.map((a, i) => (<span key={i} className="text-xs bg-white border border-border rounded-full px-2 py-1">{a}</span>))}</div>
              <Button onClick={apply} className="w-full bg-sage text-white hover:bg-sage/90 lift" data-testid="ai-apply-btn"><CheckSquare className="w-4 h-4 mr-1"/>{t("dash.ai_apply")}</Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
